// ─────────────────────────────────────────────────────────────────────────────
// Plan Management — INVOICE ENTRY.
//
// This is the screen a plan manager lives in. Everything here is shaped by how
// the work actually arrives and by what the server will accept:
//
//  1. CASCADE, not a form. An invoice arrives FROM a provider, so provider is
//     the first field; participant narrows it; the plan/service contract is
//     what the money is drawn against. Each step scopes the next.
//
//  2. THE HEADER COMMITS FIRST, then the grid goes live. That is not a UI
//     preference — /invoices/:invId/line-check and /invoices/:invId/lines both
//     need an invoice id. Creating the header up front is the only way to give
//     an operator real budget answers WHILE typing instead of at save time.
//
//  3. A LINE GRID, not a line form. Tab crosses cells, Enter commits the row
//     and opens the next one, Ctrl+D duplicates the row above. Twenty lines
//     without touching the mouse.
//
//  4. THE BUDGET POSITION NEVER LEAVES THE SCREEN. It rides in the toolbar, so
//     it is visible whatever the viewport. Remaining in the plan, remaining in
//     the category, and what both become if this invoice is saved.
//
// Rules the server enforces and this screen must not contradict:
//   • No document → cannot be approved, claimed or paid (evidenceBlock). So the
//     attachment sits in the header, and its absence is stated in rose the whole
//     time rather than discovered at approval.
//   • Exactly one of quantity OR hours, chosen by the item's unit of measure
//     (ndiaClaimFile.buildClaimRows). The measure toggle makes them exclusive.
//   • GST is P1/P2/P5, P2 default. Claim type is BLANK for a direct service.
//   • Over the price cap is a BLOCK. The API will happily store an over-cap line
//     and flag it; approval then fails. Catching it at the keystroke is cheaper.
//
// House rules from ui.jsx: colour encodes STATE only (rose = blocked/over,
// amber = attention, emerald = within budget); primary means selection/focus and
// nothing else. Money is tabular and right-aligned. Real loading/empty/error
// states, and the API's own message wherever it wrote one.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle, ArrowRight, Check, CheckCircle2, ChevronRight, Copy, FileText, List, Loader2,
  Paperclip, Plus, Receipt, RefreshCw, ShieldAlert, Sparkles, Trash2, Users, X,
} from 'lucide-react';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  BlockReasonList, Currency, EmptyState, ErrorState, Field, MicroLabel, MoneyStat,
  SectionCard, StatRow, TableSkeleton, Toolbar, Tooltip, fmtDate, money,
} from './ui';

const PM = '/plan-management';

// The API writes the message that matters — the role gate, the feature gate, the
// budget arithmetic and RLS all say something different, and a generic string
// hides which of them stopped the operator.
const errMsg = (e, fallback) => (e && e.response && e.response.data && e.response.data.message) || fallback;
const httpStatus = (e) => (e && e.response && e.response.status) || 0;

/* ── NDIA vocabularies ─────────────────────────────────────────────────────
   Mirrors backend/utils/ndiaClaimFile.js. Kept as literals rather than fetched
   because the claim file refuses anything outside these lists, so the picker
   must offer exactly them and nothing else. */
const GST_CODES = [
  { value: 'P1', label: 'P1 · GST applies' },
  { value: 'P2', label: 'P2 · GST-free' },
  { value: 'P5', label: 'P5 · Out of scope' },
];
// Blank FIRST and blank by default: a normal service carries no claim type at
// all. Defaulting to a code is how 'STD' — not an NDIA value — got into claims.
const CLAIM_TYPES = [
  { value: '', label: '— direct service' },
  { value: 'CANC', label: 'CANC · Cancellation' },
  { value: 'REPW', label: 'REPW · Report writing' },
  { value: 'TRAN', label: 'TRAN · Transport' },
  { value: 'NF2F', label: 'NF2F · Non face-to-face' },
  { value: 'THLT', label: 'THLT · Telehealth' },
  { value: 'IRSS', label: 'IRSS · Irregular SIL' },
];
const CANCELLATION_REASONS = [
  { value: 'NSDH', label: 'NSDH · No show — health' },
  { value: 'NSDF', label: 'NSDF · No show — family' },
  { value: 'NSDT', label: 'NSDT · No show — transport' },
  { value: 'NSDO', label: 'NSDO · No show — other' },
];

// Every gate, in the order backend GATE_META declares them, so the
// post-save panel reads the same way every time regardless of result order.
const GATE_ORDER = ['price_cap', 'budget', 'date', 'claim_window', 'claim_expired', 'duplicate', 'attachment',
  'provider', 'abn', 'totals', 'quantity_hours', 'gst', 'claim_type', 'pm_recorded'];
const GATE_LABEL = {
  price_cap: 'Price within limit', budget: 'Funding available', date: 'Service dates',
  claim_window: 'Claim time limit', claim_expired: 'Claim period expired', duplicate: 'Not a duplicate', attachment: 'Document attached',
  provider: 'Provider complete', abn: 'ABN', totals: 'Totals add up',
  quantity_hours: 'Quantity or hours', gst: 'GST code', claim_type: 'Claim type',
  pm_recorded: 'Plan manager recorded',
};

/* ── small pure helpers (module scope so effects never depend on them) ────── */

const uid = () => Math.random().toString(36).slice(2, 10);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const todayISO = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Hours accept either 2.5 or 2:30 — operators read invoices written both ways.
// Stored as a decimal; the claim file renders HHH:MM from it.
const parseHours = (v) => {
  if (v == null || v === '') return 0;
  const s = String(v).trim();
  const hm = s.match(/^(\d{1,3}):([0-5]?\d)$/);
  if (hm) return Number(hm[1]) + Number(hm[2]) / 60;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const toHHMM = (dec) => {
  const n = Number(dec);
  if (!Number.isFinite(n) || n <= 0) return '';
  const mins = Math.round(n * 60);
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
};

// One row's money. For an hourly row the units ARE the hours — the server
// computes line_total as qty x unit_price, so an hourly row sends its hours as
// qty too (see buildLineBody). That is not double-entry: the claim file emits
// Hours and leaves Quantity blank whenever the unit of measure is hourly.
const unitsOf = (r) => (r.measure === 'hours' ? parseHours(r.hours) : Number(r.qty) || 0);
const amountOf = (r) => (r.state === 'saved' && r.line_total != null
  ? round2(r.line_total)
  : round2(unitsOf(r) * (Number(r.unit_price) || 0)));

const isComplete = (r) => !!(r.support_item_code && r.item_date && unitsOf(r) > 0 && Number(r.unit_price) > 0);

/**
 * Why a row cannot be committed. Pure, so it can be called during render for the
 * inline note and again on Enter without the two ever disagreeing.
 *
 * `headroom` / `catHeadroom` are what is left AFTER every other row on this
 * invoice, because line-check's remaining figures exclude this invoice whole.
 */
function rowBlock(r, check, headroom, catHeadroom) {
  if (!r.support_item_code) return { tone: 'slate', text: 'Pick a support item' };
  if (!r.item_date) return { tone: 'slate', text: 'Service date needed' };
  if (unitsOf(r) <= 0) return { tone: 'slate', text: r.measure === 'hours' ? 'Hours needed' : 'Quantity needed' };
  if (!(Number(r.unit_price) > 0)) return { tone: 'slate', text: 'Unit price must be above zero' };
  if (r.claim_type === 'CANC' && !r.cancellation_reason) {
    return { tone: 'rose', text: 'A cancellation claim needs a reason' };
  }
  if (r.claim_type !== 'CANC' && r.cancellation_reason) {
    return { tone: 'rose', text: 'A cancellation reason is only allowed on a CANC line' };
  }
  if (check && check.date_error) return { tone: 'rose', text: check.date_error };
  // A real block, not a warning to click past: an over-cap line is stored with
  // over_cap=true and then refuses to approve, so it is only ever a wasted trip.
  if (check && check.cap != null && Number(r.unit_price) > Number(check.cap) + 0.005) {
    return { tone: 'rose', text: `Over the price limit of ${money(check.cap)} — cap the line or ask the provider for a corrected invoice` };
  }
  const amt = amountOf(r);
  // funding:true marks the two blocks the server will now ACCEPT-AND-HOLD rather than refuse, so
  // commitRow can let them through while still refusing everything else that is rose.
  if (catHeadroom != null && amt > catHeadroom + 0.005) {
    const catLabel = check && check.category_remaining ? check.category_remaining.label : 'category';
    return { tone: 'amber', funding: true, text: `Only ${money(catHeadroom)} left in ${catLabel} — this line will be recorded and held` };
  }
  if (headroom != null && amt > headroom + 0.005) {
    return { tone: 'amber', funding: true, text: `Only ${money(headroom)} left in the plan — this line will be recorded and held` };
  }
  return null;
}

/* ── shared control styling (matched to ui.jsx SearchInput) ───────────────── */

const CTRL = 'h-8 px-2 bg-white border border-slate-200 rounded-lg text-[12.5px] text-slate-800 '
  + 'hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 '
  + 'transition-colors motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed';

// Grid cells are borderless until focused — twenty rows of boxed inputs is a
// wall. The focus ring is the only chrome, which is also the a11y requirement.
const CELL = 'w-full h-7 px-1.5 bg-transparent border border-transparent rounded-md text-[12.5px] text-slate-800 '
  + 'hover:border-slate-200 focus:bg-white focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 '
  + 'transition-colors motion-reduce:transition-none disabled:opacity-40 disabled:cursor-not-allowed';

function Kbd({ children }) {
  return <kbd className="inline-flex items-center h-[18px] px-1 rounded border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-500 leading-none">{children}</kbd>;
}

// A 403 is an answer, not a failure — retrying cannot change it, so no Retry.
function AccessDenied({ message }) {
  return (
    <EmptyState
      icon={ShieldAlert}
      title="You do not have access to invoice entry"
      hint={message || 'Ask an administrator to grant you a plan-management role.'}
    />
  );
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/* ── Suggest: portalled dropdown ──────────────────────────────────────────────
   The grid scrolls horizontally, and an `overflow-x:auto` box clips its children
   in BOTH axes, so an in-flow dropdown would be cut off at the row. Fixed
   position in a portal is the same trick ui.jsx OverflowMenu uses. */

function Suggest({ open, anchorRef, items, active, onPick, loading, emptyText, width = 380 }) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const w = Math.max(r.width, width);
      setPos({
        left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)),
        top: r.bottom + 4,
        width: w,
        maxHeight: Math.max(120, window.innerHeight - r.bottom - 16),
      });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, anchorRef, width]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      role="listbox"
      style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight, zIndex: 70 }}
      className="overflow-y-auto overscroll-contain bg-white rounded-xl border border-slate-200 shadow-card-deep py-1 animate-fade-in motion-reduce:animate-none"
      onMouseDown={(e) => e.preventDefault() /* keep focus in the input */}
    >
      {loading && <p className="px-3 py-2 text-[12px] text-slate-400 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin motion-reduce:animate-none" />Searching…</p>}
      {!loading && !items.length && <p className="px-3 py-2 text-[12px] text-slate-400">{emptyText}</p>}
      {items.map((it, i) => (
        <button
          key={it.value} type="button" role="option" aria-selected={i === active}
          onClick={() => onPick(it)}
          className={clsx('w-full text-left px-3 py-1.5 flex items-baseline gap-2 transition-colors motion-reduce:transition-none',
            i === active ? 'bg-primary-50 text-primary-800' : 'text-slate-700 hover:bg-slate-50')}
        >
          <span className="text-[12.5px] font-medium truncate flex-1">{it.label}</span>
          {it.meta && <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{it.meta}</span>}
        </button>
      ))}
    </div>,
    document.body,
  );
}

/* ── Combo: type-to-filter picker over a loaded list ───────────────────────── */

function Combo({ value, options, onChange, placeholder, disabled, inputRef, id, invalid }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ownRef = useRef(null);
  const ref = inputRef || ownRef;

  const picked = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options.slice(0, 60);
    return options.filter((o) => o.label.toLowerCase().includes(needle)
      || String(o.search || '').toLowerCase().includes(needle)).slice(0, 60);
  }, [options, q]);

  const commit = (opt) => {
    onChange(opt ? opt.value : '');
    setQ('');
    setOpen(false);
    setActive(0);
  };

  return (
    <div className="relative">
      <input
        id={id} ref={ref} type="text" role="combobox" aria-expanded={open} aria-autocomplete="list"
        disabled={disabled} placeholder={picked ? picked.label : placeholder}
        value={open ? q : (picked ? picked.label : '')}
        onFocus={() => { setOpen(true); setQ(''); setActive(0); }}
        onBlur={() => setOpen(false)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, matches.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter' && open && matches[active]) { e.preventDefault(); commit(matches[active]); }
          else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
          else if ((e.key === 'Backspace' || e.key === 'Delete') && !q && value) { commit(null); }
        }}
        className={clsx(CTRL, 'w-full', invalid && 'border-rose-300 focus:border-rose-400 focus:ring-rose-100',
          picked && !open && 'text-slate-900')}
      />
      <Suggest
        open={open} anchorRef={ref} items={matches} active={active} onPick={commit}
        emptyText={options.length ? 'Nothing matches' : 'Nothing to choose from yet'}
      />
    </div>
  );
}

/* ── ItemCell: support-item typeahead inside the grid ─────────────────────────
   Two sources, deliberately. With a plan chosen we use the PLAN's own price book
   (/contracts/:planId/services) because it carries the cap and the NDIS support
   category, which is what the budget is drawn against. Without one we fall back
   to /fee-item-search over the active book, which returns code/name/rate only. */

function ItemCell({ row, planId, onPick, registerInput, blocked }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounced = useDebounced(q, 250);
  // One stable ref object: Suggest measures against it, and the grid registers
  // the same element so Enter can hand focus to the next row.
  const inputRef = useRef(null);
  const setRef = (el) => { inputRef.current = el; if (registerInput) registerInput(el); };

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    const url = planId ? `${PM}/contracts/${planId}/services` : `${PM}/fee-item-search`;
    const params = planId ? { search: debounced } : { q: debounced };
    api.get(url, { params })
      .then(({ data }) => {
        if (!alive) return;
        const list = planId ? (data && data.items) || [] : (Array.isArray(data) ? data : []);
        setItems(list.slice(0, 40));
      })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, debounced, planId]);

  const options = items.map((it) => ({
    value: it.code,
    label: `${it.code} — ${it.name || ''}`.trim(),
    meta: it.cap != null ? money(it.cap) : (it.rate != null ? money(it.rate) : ''),
    raw: it,
  }));

  const choose = (opt) => {
    const it = opt.raw;
    onPick({
      support_item_code: it.code,
      support_item_name: it.name || '',
      support_category: it.category || '',
      // The book rate is the price cap, so it is the right opening bid — the
      // operator overtypes it when the provider billed less.
      unit_price: it.cap != null ? it.cap : (it.rate != null ? it.rate : ''),
    });
    setQ('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        ref={setRef} type="text" aria-label="Support item" role="combobox" aria-expanded={open}
        placeholder={row.support_item_code || 'Item #'}
        value={open ? q : (row.support_item_code || '')}
        onFocus={() => { setOpen(true); setQ(''); setActive(0); }}
        onBlur={() => setOpen(false)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter' && open && options[active]) {
            // Enter inside the open list PICKS; it must not also fall through to
            // the row handler and commit a half-typed line.
            e.preventDefault(); e.stopPropagation(); choose(options[active]);
          } else if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
        }}
        className={clsx(CELL, 'font-medium tabular-nums', blocked && 'border-rose-200')}
      />
      {row.support_item_name && !open && (
        <span className="block px-1.5 text-[10.5px] text-slate-400 truncate leading-tight">{row.support_item_name}</span>
      )}
      <Suggest
        open={open} anchorRef={inputRef} items={options} active={active} onPick={choose}
        loading={loading} width={460}
        emptyText={q ? 'No support item matches' : 'Type a code or a name'}
      />
    </div>
  );
}

/* ── Attachment ───────────────────────────────────────────────────────────────
   First class, not an afterthought. The file becomes a data: URL in
   `document_url`; the server hashes and versions data: URLs into the evidence
   register exactly as it does files under /uploads. Attach-once: PUT refuses to
   swap a document that is already recorded (record retention), so the control
   locks after the header is created with one. */

const MAX_DOC_BYTES = 9 * 1024 * 1024; // server body limit is 15mb; base64 inflates ~33%

function AttachmentField({ doc, onDoc, onScan, scanning, locked }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const take = (file) => {
    if (!file) return;
    if (file.size > MAX_DOC_BYTES) {
      toast.error(`${file.name} is ${(file.size / 1048576).toFixed(1)} MB — the limit is 9 MB.`);
      return;
    }
    const r = new FileReader();
    r.onload = () => onDoc({ url: String(r.result), name: file.name, size: file.size, type: file.type });
    r.onerror = () => toast.error('Could not read that file');
    r.readAsDataURL(file);
  };

  if (doc) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 h-8">
        <Paperclip size={13} className="text-emerald-600 shrink-0" />
        <span className="text-[12px] font-medium text-emerald-800 truncate flex-1">{doc.name}</span>
        {doc.size ? <span className="text-[11px] text-emerald-600 tabular-nums shrink-0">{(doc.size / 1024).toFixed(0)} KB</span> : null}
        {!locked && (
          <>
            {onScan && (
              <Tooltip label="Read this invoice and prefill the lines">
                <button type="button" onClick={onScan} disabled={scanning}
                  className="p-1 rounded text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                  {scanning ? <Loader2 size={13} className="animate-spin motion-reduce:animate-none" /> : <Sparkles size={13} />}
                </button>
              </Tooltip>
            )}
            <button type="button" onClick={() => onDoc(null)} aria-label="Remove attachment"
              className="p-1 rounded text-emerald-700 hover:bg-emerald-100"><X size={13} /></button>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}
    >
      <input ref={inputRef} type="file" className="sr-only" accept=".pdf,image/*"
        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; take(f); }} />
      <button
        type="button" onClick={() => inputRef.current && inputRef.current.click()}
        className={clsx('w-full h-8 px-3 flex items-center gap-2 rounded-lg border border-dashed text-[12px] font-medium',
          'transition-colors motion-reduce:transition-none focus:outline-none focus:ring-2 focus:ring-primary-100',
          drag ? 'border-primary-400 bg-primary-50 text-primary-700'
            : 'border-rose-300 bg-rose-50/50 text-rose-700 hover:bg-rose-50')}
      >
        <Paperclip size={13} className="shrink-0" />
        <span className="truncate">Attach the provider invoice — drop a file or browse</span>
      </button>
    </div>
  );
}

/* ── Clone picker ─────────────────────────────────────────────────────────── */

function ClonePicker({ clientId, providerId, onClose, onClone }) {
  const [state, setState] = useState({ loading: true, error: null, rows: [] });
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, rows: [] });
    api.get(`${PM}/invoices`, { params: { client_id: clientId } })
      .then(({ data }) => { if (alive) setState({ loading: false, error: null, rows: Array.isArray(data) ? data : [] }); })
      .catch((e) => { if (alive) setState({ loading: false, error: errMsg(e, 'Could not load earlier invoices for this participant'), rows: [] }); });
    return () => { alive = false; };
  }, [clientId]);

  // Same provider first — a repeating pattern is a provider's pattern.
  const rows = useMemo(() => {
    const all = state.rows.filter((r) => r.status !== 'cancelled' && Number(r.line_count) > 0);
    if (!providerId) return all;
    const mine = all.filter((r) => r.provider_id === providerId);
    return mine.length ? mine : all;
  }, [state.rows, providerId]);

  const clone = async (inv) => {
    setBusy(inv.id);
    try {
      const { data } = await api.get(`${PM}/invoices/${inv.id}`);
      onClone(data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not read that invoice'));
    } finally { setBusy(null); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 animate-fade-in motion-reduce:animate-none" onMouseDown={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Clone an invoice" onMouseDown={(e) => e.stopPropagation()}
        className="h-full w-full max-w-lg bg-white flex flex-col shadow-modal animate-drawer-in motion-reduce:animate-none">
        <header className="px-5 py-3.5 border-b border-slate-200 flex items-start gap-3 shrink-0">
          <span className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center shrink-0"><Copy size={16} /></span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[15px] font-semibold text-slate-900">Clone an invoice</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Copies the lines — item, rate, dates, GST and claim type. The invoice number is never
              copied, and every cloned date is flagged until you confirm it.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="p-1.5 -mr-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          {state.loading && <TableSkeleton rows={6} cols={4} />}
          {!state.loading && state.error && <ErrorState message={state.error} />}
          {!state.loading && !state.error && !rows.length && (
            <EmptyState icon={Receipt} title="Nothing to clone yet"
              hint="This participant has no invoice with lines on it. Enter this one by hand and it becomes the pattern for the next." />
          )}
          {!state.loading && !state.error && rows.map((r) => (
            <button key={r.id} type="button" onClick={() => clone(r)} disabled={!!busy}
              className="w-full text-left px-5 py-3 border-b border-slate-100 flex items-center gap-3 hover:bg-slate-50 disabled:opacity-50 transition-colors motion-reduce:transition-none focus:outline-none focus-visible:bg-primary-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-slate-800 truncate">
                  {r.provider_display || 'Unnamed provider'}
                  {r.provider_invoice_number ? <span className="text-slate-400 font-normal"> · #{r.provider_invoice_number}</span> : null}
                </p>
                <p className="text-[11.5px] text-slate-400 mt-0.5">
                  {fmtDate(r.invoice_date)} · {r.line_count} {Number(r.line_count) === 1 ? 'line' : 'lines'}
                </p>
              </div>
              <Currency value={r.total_amount} className="text-[13px] font-semibold text-slate-700 shrink-0" />
              {busy === r.id
                ? <Loader2 size={14} className="text-slate-400 animate-spin motion-reduce:animate-none shrink-0" />
                : <ChevronRight size={14} className="text-slate-300 shrink-0" />}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── The screen ───────────────────────────────────────────────────────────── */

const blankRow = (seed) => ({
  key: uid(), id: null, state: 'draft', error: null, cloned: false, line_total: null,
  support_item_code: '', support_item_name: '', support_category: '',
  item_date: '', measure: 'qty', qty: '1', hours: '',
  unit_price: '', gst_code: 'P2', claim_type: '', cancellation_reason: '',
  ...(seed || {}),
});

const emptyHeader = (seed) => ({
  provider_id: '', client_id: '', plan_id: '',
  provider_invoice_number: '', invoice_date: todayISO(),
  ...(seed || {}),
});

export default function InvoiceEntry({ onOpenInvoice, onExit }) {
  /* pickers — the cascade's three sources */
  const [pickers, setPickers] = useState({ loading: true, error: null, forbidden: false, providers: [], participants: [] });
  const [pickerNonce, setPickerNonce] = useState(0);
  const [contracts, setContracts] = useState({ loading: false, error: null, list: [] });

  /* header */
  const [head, setHead] = useState(emptyHeader);
  const [doc, setDoc] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [dupe, setDupe] = useState(null);           // 409 payload awaiting a decision

  /* the live invoice + its grid */
  const [invoice, setInvoice] = useState(null);     // { id, ... } once the header is committed
  const [rows, setRows] = useState(() => [blankRow()]);
  const [checks, setChecks] = useState({});         // row key → line-check result
  const [checkNonce, setCheckNonce] = useState(0);  // bumped whenever committed money changes
  const [pendingFocus, setPendingFocus] = useState(null);
  const [cloneOpen, setCloneOpen] = useState(false);

  /* post-save */
  const [finishing, setFinishing] = useState(false);
  const [result, setResult] = useState(null);       // { invoice, blocks, gates }

  const itemRefs = useRef({});
  const providerRef = useRef(null);
  // Mirrors `rows` so callbacks that only need to READ them do not have to list
  // `rows` as a dependency and churn on every keystroke.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  /* ── load the pickers ────────────────────────────────────────────────────── */
  useEffect(() => {
    let alive = true;
    setPickers((p) => ({ ...p, loading: true }));
    Promise.all([api.get(`${PM}/providers`), api.get(`${PM}/participants`)])
      .then(([p, c]) => {
        if (!alive) return;
        setPickers({
          loading: false, error: null, forbidden: false,
          providers: Array.isArray(p.data) ? p.data : [],
          participants: Array.isArray(c.data) ? c.data : [],
        });
      })
      .catch((e) => {
        if (!alive) return;
        setPickers({
          loading: false, forbidden: httpStatus(e) === 403,
          error: errMsg(e, 'Could not load providers and participants'),
          providers: [], participants: [],
        });
      });
    return () => { alive = false; };
  }, [pickerNonce]);

  /* ── participant → their service contracts ───────────────────────────────── */
  useEffect(() => {
    if (!head.client_id) { setContracts({ loading: false, error: null, list: [] }); return undefined; }
    let alive = true;
    setContracts({ loading: true, error: null, list: [] });
    api.get(`${PM}/participants/${head.client_id}/profile`)
      .then(({ data }) => {
        if (!alive) return;
        setContracts({ loading: false, error: null, list: (data && data.contracts) || [] });
      })
      .catch((e) => {
        if (!alive) return;
        setContracts({ loading: false, error: errMsg(e, 'Could not load the plans for this participant'), list: [] });
      });
    return () => { alive = false; };
  }, [head.client_id]);

  // Auto-select when there is exactly one live contract — a plan manager should
  // not have to confirm the only possible answer.
  useEffect(() => {
    const live = contracts.list.filter((c) => c.status === 'Active');
    if (live.length === 1) setHead((h) => (h.plan_id ? h : { ...h, plan_id: live[0].id }));
  }, [contracts.list]);

  /* ── live line-check ──────────────────────────────────────────────────────
     One effect for the whole grid, keyed on a JSON signature of the rows worth
     checking. Signing the rows rather than looping effects keeps the dependency
     array complete and honest — no lint escape hatch — and leaves exactly one
     timer to clean up. Price is deliberately NOT in the signature: line-check
     takes no amount, and the cap comparison happens client-side against the cap
     it already returned, so re-fetching on every price keystroke buys nothing. */
  const probeSig = useMemo(() => JSON.stringify(
    rows.filter((r) => r.support_item_code && r.item_date && amountOf(r) > 0)
      .map((r) => [r.key, r.support_item_code, r.item_date, r.support_category || '']),
  ), [rows]);

  useEffect(() => {
    const invId = invoice && invoice.id;
    if (!invId) return undefined;
    const probes = JSON.parse(probeSig);
    if (!probes.length) return undefined;
    let alive = true;
    const t = setTimeout(async () => {
      for (let i = 0; i < probes.length; i += 1) {
        const [key, code, date, category] = probes[i];
        try {
          // Sequential on purpose: each answer is the budget as of the previous
          // one, and firing them together would race the same plan row.
          const { data } = await api.get(`${PM}/invoices/${invId}/line-check`, {
            params: { code, date, category: category || undefined },
          });
          if (!alive) return;
          setChecks((c) => ({ ...c, [key]: data || {} }));
        } catch (e) {
          if (!alive) return;
          setChecks((c) => ({ ...c, [key]: { failed: errMsg(e, 'Could not check this line') } }));
        }
      }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [invoice, probeSig, checkNonce]);

  /* ── focus handoff (Enter → next row) ────────────────────────────────────── */
  useEffect(() => {
    if (!pendingFocus) return;
    const el = itemRefs.current[pendingFocus];
    if (el) el.focus();
    setPendingFocus(null);
  }, [pendingFocus]);

  /* ── derived: options ────────────────────────────────────────────────────── */
  const providerOpts = useMemo(() => pickers.providers.map((p) => ({
    value: p.id, label: p.name, search: p.abn || '',
    meta: p.bank_verified === false ? 'bank unverified' : '',
  })), [pickers.providers]);

  const participantOpts = useMemo(() => pickers.participants.map((c) => ({
    value: c.id, label: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
    search: c.ndis_number || '', meta: c.ndis_number || '',
  })), [pickers.participants]);

  const contractOpts = useMemo(() => contracts.list.map((c) => ({
    value: c.id,
    label: `${c.ref} · ${fmtDate(c.start_date)} – ${fmtDate(c.end_date)}${c.status === 'Active' ? '' : ` (${c.status})`}`,
    search: c.ref, meta: money(c.remaining),
  })), [contracts.list]);

  const chosenProvider = useMemo(() => pickers.providers.find((p) => p.id === head.provider_id) || null, [pickers.providers, head.provider_id]);
  const chosenParticipant = useMemo(() => pickers.participants.find((c) => c.id === head.client_id) || null, [pickers.participants, head.client_id]);
  const chosenContract = useMemo(() => contracts.list.find((c) => c.id === head.plan_id) || null, [contracts.list, head.plan_id]);

  /* ── derived: money ──────────────────────────────────────────────────────── */
  const invoiceTotal = useMemo(() => round2(rows.reduce((a, r) => a + amountOf(r), 0)), [rows]);
  const savedTotal = useMemo(() => round2(rows.filter((r) => r.state === 'saved').reduce((a, r) => a + amountOf(r), 0)), [rows]);

  // line-check's remaining figures EXCLUDE this invoice, so they are a stable
  // "before" number no matter how many lines have already been committed.
  const planRemaining = useMemo(() => {
    const vals = Object.values(checks).map((c) => (c && c.plan_remaining != null ? Number(c.plan_remaining) : null)).filter((n) => n != null);
    if (vals.length) return vals[vals.length - 1];
    return chosenContract ? Number(chosenContract.remaining) : null;
  }, [checks, chosenContract]);

  // Every category funded on the chosen plan, with what is left in it right now. The line's
  // category picker is built from this, so an operator chooses against the real balance instead
  // of picking a category and being refused at save time. Refreshed whenever the plan changes or
  // a line is committed, because committing a line moves the balance.
  const [catPositions, setCatPositions] = useState([]);
  const [participantMatch, setParticipantMatch] = useState(null);
  // The most recent funding shortfall, kept so the banner explains the hold after the toast goes.
  const [heldNotice, setHeldNotice] = useState(null);
  const confirm = useConfirm();

  const loadCatPositions = useCallback(() => {
    if (!head.plan_id) { setCatPositions([]); return; }
    const params = invoice && invoice.id ? { exclude: invoice.id } : {};
    api.get(`${PM}/plans/${head.plan_id}/category-positions`, { params })
      .then(({ data }) => setCatPositions(Array.isArray(data && data.categories) ? data.categories : []))
      // A picker that cannot load its balances must not block the line — the server-side budget
      // gate still refuses an over-spend on save, so this degrades to a plain list.
      .catch(() => setCatPositions([]));
  }, [head.plan_id, invoice]);

  useEffect(() => { loadCatPositions(); }, [loadCatPositions]);

  // Everything on this invoice that is over a budget, named and totalled. The per-line notes are
  // small and sit under the cell being typed in; on a twenty-line invoice an operator can fill the
  // grid without ever looking at the one that is over. This is the summary that cannot be missed,
  // and it is what the finish action checks before committing.
  const overBudget = useMemo(() => {
    const byCat = new Map();
    rows.forEach((r) => {
      if (!r.support_category) return;
      const cur = byCat.get(r.support_category) || 0;
      byCat.set(r.support_category, round2(cur + amountOf(r)));
    });
    const cats = [];
    byCat.forEach((drawn, key) => {
      const pos = catPositions.find((c) => c.category === key);
      if (!pos) return;
      const over = round2(drawn - Number(pos.remaining));
      if (over > 0.005) cats.push({ key, label: pos.label, remaining: Number(pos.remaining), drawn, over });
    });
    const invoiceTotal = rows.reduce((a, r) => a + amountOf(r), 0);
    const planOver = planRemaining != null ? round2(invoiceTotal - planRemaining) : 0;
    return { cats, planOver: planOver > 0.005 ? planOver : 0, planRemaining, invoiceTotal: round2(invoiceTotal) };
  }, [rows, catPositions, planRemaining]);

  // What each category has left AFTER the other lines on this invoice are counted — the number
  // the operator actually needs while typing the next line.
  const catRemainingAfterInvoice = useCallback((categoryKey, exceptRowKey) => {
    const pos = catPositions.find((c) => c.category === categoryKey);
    if (!pos) return null;
    const drawn = rows.reduce((a, r) => (
      r.key === exceptRowKey || r.support_category !== categoryKey ? a : a + amountOf(r)
    ), 0);
    return round2(Number(pos.remaining) - drawn);
  }, [catPositions, rows]);

  // Categories this invoice actually touches, each with its own before/after.
  const categoryPositions = useMemo(() => {
    const byLabel = new Map();
    rows.forEach((r) => {
      const c = checks[r.key];
      if (!c || !c.category_remaining) return;
      const { label, remaining } = c.category_remaining;
      const cur = byLabel.get(label) || { label, remaining: Number(remaining) || 0, draw: 0 };
      cur.draw = round2(cur.draw + amountOf(r));
      byLabel.set(label, cur);
    });
    return [...byLabel.values()];
  }, [rows, checks]);

  const headroomFor = useCallback((row) => {
    if (planRemaining == null) return null;
    const others = rows.reduce((a, r) => (r.key === row.key ? a : a + amountOf(r)), 0);
    return round2(planRemaining - others);
  }, [planRemaining, rows]);

  const catHeadroomFor = useCallback((row) => {
    const c = checks[row.key];
    if (!c || !c.category_remaining) return null;
    const label = c.category_remaining.label;
    const others = rows.reduce((a, r) => {
      if (r.key === row.key) return a;
      const rc = checks[r.key];
      return rc && rc.category_remaining && rc.category_remaining.label === label ? a + amountOf(r) : a;
    }, 0);
    return round2(Number(c.category_remaining.remaining) - others);
  }, [checks, rows]);

  /* ── header actions ──────────────────────────────────────────────────────── */

  const canStart = !!(head.provider_id && head.client_id);

  // OCR prefill. /invoices/extract?preview=true reads the file and matches each
  // line against the plan's price book without creating anything — it is a
  // prefill, and the operator still confirms every row.
  const scanDoc = useCallback(async () => {
    if (!doc) return;
    setScanning(true);
    try {
      const b64 = String(doc.url).split(',')[1];
      const { data } = await api.post(`${PM}/invoices/extract`, {
        file: b64, media_type: doc.type || undefined, preview: true,
        client_id: head.client_id || undefined,
      });
      const d = data || {};
      setHead((h) => ({
        ...h,
        client_id: h.client_id || d.client_id || '',
        plan_id: h.plan_id || d.plan_id || '',
        // The provider was read off the invoice but never applied, so the operator re-picked it
        // on every scan even when the ABN matched exactly.
        provider_id: h.provider_id || d.provider_id || '',
        provider_invoice_number: h.provider_invoice_number || d.provider_invoice_number || '',
        invoice_date: d.invoice_date || h.invoice_date,
      }));
      // Who the invoice says it is for. When that did not resolve to a participant, keep it so the
      // screen can say so and offer the near matches, instead of leaving Client blank in silence.
      setParticipantMatch(d.participant_match || null);
      const scanned = (d.lines || []).map((l) => blankRow({
        support_item_code: l.support_item_code || '',
        support_item_name: l.support_item_name || '',
        support_category: l.support_category || '',
        item_date: (l.item_date || '').slice(0, 10),
        qty: l.qty != null ? String(l.qty) : '1',
        unit_price: l.unit_price != null ? String(l.unit_price) : '',
        cloned: true, // same treatment as a clone: every date is unconfirmed
      }));
      if (scanned.length) setRows(scanned.concat(blankRow()));
      const pmatch = d.participant_match;
      if (pmatch && !pmatch.matched) {
        // Say it plainly and name who the invoice was for — the operator's next step depends on
        // whether nobody was named, nobody matched, or several did.
        const who = pmatch.extracted_name || pmatch.extracted_ndis;
        toast(who
          ? `Read ${scanned.length} line(s). This invoice is for "${who}" — choose the participant below.`
          : `Read ${scanned.length} line(s). The invoice does not name a participant — choose one below.`,
        { icon: '⚠️', duration: 7000 });
      } else {
        toast.success(`Read ${scanned.length} ${scanned.length === 1 ? 'line' : 'lines'} — confirm each one`);
      }
    } catch (e) {
      toast.error(errMsg(e, 'Could not read that file'));
    } finally { setScanning(false); }
  }, [doc, head.client_id]);

  const startInvoice = useCallback(async (force) => {
    if (!canStart) { toast.error('Choose the provider and the participant first'); return; }
    setStarting(true);
    setDupe(null);
    try {
      const { data } = await api.post(`${PM}/invoices`, {
        client_id: head.client_id,
        plan_id: head.plan_id || null,
        provider_id: head.provider_id || null,
        provider_name: chosenProvider ? chosenProvider.name : null,
        provider_invoice_number: head.provider_invoice_number || null,
        invoice_date: head.invoice_date || null,
        document_url: doc ? doc.url : null,
        force: force === true,
      });
      setInvoice(data);
      if (data.duplicate_warning) toast(data.duplicate_warning, { icon: '⚠️' });
      if (data.coi_warning) toast(data.coi_warning, { icon: '⚠️' });
      const first = rowsRef.current[0];
      setPendingFocus(first ? first.key : null);
    } catch (e) {
      // The API returns { message, duplicate } — NOT a `code`. Show what it
      // matched and let the operator choose, rather than silently forcing.
      if (httpStatus(e) === 409 && e.response.data && e.response.data.duplicate) {
        setDupe({ message: e.response.data.message, match: e.response.data.duplicate });
      } else {
        toast.error(errMsg(e, 'Could not start this invoice'));
      }
    } finally { setStarting(false); }
  }, [canStart, head, chosenProvider, doc]);

  // Attaching later is allowed; replacing is not (PUT refuses to swap a document
  // that is already recorded), so this only ever runs on an invoice with none.
  const attachLater = useCallback(async (next) => {
    setDoc(next);
    if (!invoice || !next) return;
    try {
      await api.put(`${PM}/invoices/${invoice.id}`, { document_url: next.url });
      setInvoice((v) => ({ ...v, document_url: next.url }));
      toast.success('Document attached');
    } catch (e) {
      toast.error(errMsg(e, 'Could not attach that document'));
      setDoc(null);
    }
  }, [invoice]);

  /* ── grid actions ────────────────────────────────────────────────────────── */

  const patchRow = useCallback((key, patch) => {
    setRows((ls) => ls.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }, []);

  const addRow = useCallback((seed) => {
    const row = blankRow(seed);
    setRows((ls) => [...ls, row]);
    setPendingFocus(row.key);
    return row;
  }, []);

  // "Duplicate line" — item, rate and date off the row above, which is the shape
  // of a real provider invoice: same support, same rate, a run of dates.
  const duplicateFrom = useCallback((key) => {
    setRows((ls) => {
      const i = ls.findIndex((r) => r.key === key);
      const src = i > 0 ? ls[i - 1] : null;
      if (!src) return ls;
      return ls.map((r) => (r.key === key ? {
        ...r,
        support_item_code: src.support_item_code, support_item_name: src.support_item_name,
        support_category: src.support_category, unit_price: src.unit_price,
        item_date: src.item_date, measure: src.measure, qty: src.qty, hours: src.hours,
        gst_code: src.gst_code, claim_type: src.claim_type, cancellation_reason: src.cancellation_reason,
        cloned: false,
      } : r));
    });
  }, []);

  const buildLineBody = (r) => {
    const hourly = r.measure === 'hours';
    const units = unitsOf(r);
    return {
      support_item_code: r.support_item_code || null,
      support_item_name: r.support_item_name || null,
      support_category: r.support_category || null,
      item_date: r.item_date || null,
      // A grid line is delivered on ONE day, so its service period is that day at both ends. Sent
      // explicitly rather than left to a server-side fallback, so every path puts the same three
      // fields on a line and the claim file's columns 3 and 4 never depend on which screen it
      // came from.
      service_date_from: r.item_date || null,
      service_date_to: r.item_date || null,
      // qty carries the billable units either way, because the server computes
      // line_total as qty x unit_price. For an hourly line `unit: 'H'` is what
      // makes the claim file emit Hours and leave Quantity blank.
      qty: units,
      unit: hourly ? 'H' : 'each',
      hours: hourly ? units : null,
      unit_price: Number(r.unit_price) || 0,
      gst_code: r.gst_code || 'P2',
      // null, not '': blank means a direct service. The column defaults to the
      // legacy 'STD', which the claim builder maps straight back to blank.
      claim_type: r.claim_type || null,
      cancellation_reason: r.claim_type === 'CANC' ? (r.cancellation_reason || null) : null,
    };
  };

  const commitRow = useCallback(async (key) => {
    const invId = invoice && invoice.id;
    if (!invId) return false;
    const row = rows.find((r) => r.key === key);
    if (!row || row.state === 'saving') return false;
    const block = rowBlock(row, checks[key], headroomFor(row), catHeadroomFor(row));
    // A FUNDING shortfall no longer stops the line being entered. The server stores it, records
    // which budget ran out and by how much, and puts the invoice on hold for manual approval — so
    // the nine funded lines on a ten-line invoice can be captured instead of the whole invoice
    // being unrecordable. Anything else rose (a price-cap breach, a bad date, a missing field) is
    // still refused here, because those are wrong at any budget.
    if (block && !block.funding) {
      if (block.tone === 'rose') toast.error(block.text);
      return false;
    }
    patchRow(key, { state: 'saving', error: null });
    const body = buildLineBody(row);
    try {
      const { data } = row.id
        ? await api.put(`${PM}/lines/${row.id}`, body)
        : await api.post(`${PM}/invoices/${invId}/lines`, body);
      const saved = row.id ? { ...body, id: row.id } : data;
      // Verify what the server actually stored rather than assuming the body
      // round-tripped: POST /lines does not currently persist cancellation_reason,
      // and a CANC line that silently lost its reason fails at claim time.
      const lostReason = body.cancellation_reason && !saved.cancellation_reason && !row.id;
      patchRow(key, {
        state: 'saved', id: saved.id || row.id,
        line_total: saved.line_total != null ? Number(saved.line_total) : amountOf(row),
        cloned: false,
        error: lostReason ? 'Saved, but the cancellation reason was not stored — record it on the invoice before claiming.' : null,
        // The server's verdict on this line: funded, or held with the reason and the numbers.
        funding: (data && data.funding) || (row.id ? row.funding : undefined),
      });
      if (data && data.warning) toast(data.warning, { icon: '⏳' });
      if (data && data.funding && data.funding.state === 'held') {
        // Say the consequence, not the tally. An adversarial review of the design flagged that
        // "line recorded" reads as partial success, when in fact NOTHING on this invoice can be
        // claimed until the shortfall is resolved.
        setHeldNotice(data.funding.detail);
        toast(`Line recorded and held — ${data.funding.detail} Nothing on this invoice will be claimed until this is resolved.`,
          { icon: '⛔', duration: 9000 });
      }
      setCheckNonce((n) => n + 1);
      // A committed line moves the category balance, so the picker's figures must move with it.
      loadCatPositions();
      return true;
    } catch (e) {
      // 400 here is the budget/date/period gate speaking, and its message names
      // the exact shortfall. Never paraphrase it.
      patchRow(key, { state: 'error', error: errMsg(e, 'This line was rejected') });
      return false;
    }
  }, [invoice, rows, checks, headroomFor, catHeadroomFor, patchRow, loadCatPositions]);

  const removeRow = useCallback(async (key) => {
    const row = rows.find((r) => r.key === key);
    if (!row) return;
    if (row.id) {
      try {
        await api.delete(`${PM}/lines/${row.id}`);
        setCheckNonce((n) => n + 1);
        loadCatPositions();   // removing a line gives that category its headroom back
      } catch (e) { toast.error(errMsg(e, 'Could not remove that line')); return; }
    }
    setRows((ls) => {
      const next = ls.filter((r) => r.key !== key);
      return next.length ? next : [blankRow()];
    });
    setChecks((c) => { const n = { ...c }; delete n[key]; return n; });
  }, [rows]);

  // Enter = commit this row and open the next. Ctrl/Cmd+D = duplicate the row
  // above into this one. Ctrl/Cmd+Backspace = drop the row.
  const onRowKeyDown = (e, row, index) => {
    const tag = e.target.tagName;
    if (tag !== 'INPUT' && tag !== 'SELECT') return;
    const mod = e.metaKey || e.ctrlKey;
    if (e.key === 'Enter' && !e.shiftKey && !mod) {
      e.preventDefault();
      commitRow(row.key).then((ok) => {
        if (!ok) return;
        const next = rows[index + 1];
        if (next) setPendingFocus(next.key);
        else addRow({ item_date: row.item_date, gst_code: row.gst_code });
      });
    } else if (mod && (e.key === 'd' || e.key === 'D')) {
      e.preventDefault();
      duplicateFrom(row.key);
    } else if (mod && (e.key === 'Backspace' || e.key === 'Delete')) {
      e.preventDefault();
      removeRow(row.key);
    }
  };

  /* ── clone ───────────────────────────────────────────────────────────────── */
  const applyClone = useCallback((src) => {
    setCloneOpen(false);
    setHead((h) => ({
      ...h,
      provider_id: h.provider_id || src.provider_id || '',
      plan_id: h.plan_id || src.plan_id || '',
      // Never the invoice number: provider + number is the duplicate key, and
      // copying it is precisely the mistake the 409 exists to catch.
      provider_invoice_number: '',
    }));
    const cloned = (src.lines || []).map((l) => blankRow({
      support_item_code: l.support_item_code || '',
      support_item_name: l.support_item_name || '',
      support_category: l.support_category || '',
      item_date: (l.item_date || '').slice(0, 10),
      measure: /^(h|hr|hour|hourly|hours)$/i.test(String(l.unit_of_measure || l.unit || '')) ? 'hours' : 'qty',
      qty: l.qty != null ? String(l.qty) : '1',
      hours: l.hours != null ? String(l.hours) : '',
      unit_price: l.unit_price != null ? String(l.unit_price) : '',
      gst_code: l.gst_code || 'P2',
      claim_type: l.claim_type && !['STD', 'STAN', 'STANDARD'].includes(String(l.claim_type).toUpperCase()) ? l.claim_type : '',
      cancellation_reason: l.cancellation_reason || '',
      cloned: true,
    }));
    setRows(cloned.length ? cloned.concat(blankRow()) : [blankRow()]);
    toast.success(`Cloned ${cloned.length} ${cloned.length === 1 ? 'line' : 'lines'} — confirm every service date`);
  }, []);

  /* ── finish ──────────────────────────────────────────────────────────────── */

  const finish = useCallback(async () => {
    const invId = invoice && invoice.id;
    if (!invId) return;
    // Last word before committing. The banner above says the same thing, but an operator working
    // down a long grid can reach Finish without having looked up — and an over-budget invoice is
    // approved, claimed, and then rejected by the NDIA days later. Not a block: a plan manager can
    // legitimately record an over-budget invoice and hold it while the provider corrects it.
    if (overBudget.cats.length > 0 || overBudget.planOver > 0) {
      const parts = overBudget.cats.map((c) => `${c.label} is ${money(c.over)} over`);
      if (overBudget.planOver > 0) parts.push(`the plan total is ${money(overBudget.planOver)} over`);
      const proceed = await confirm(
        `${parts.join(', ')}. The NDIA will not pay above the budget — this invoice will be held rather than claimed. Finish it anyway?`,
        { title: 'This invoice is over budget', danger: true },
      );
      if (!proceed) return;
    }
    setFinishing(true);
    try {
      // Commit anything still sitting as a draft, one at a time — the server
      // re-checks the budget per line inside a transaction, so order matters.
      const pending = rowsRef.current.filter((r) => r.state !== 'saved' && isComplete(r));
      for (let i = 0; i < pending.length; i += 1) {
        await commitRow(pending[i].key);
      }
      const [inv, gates] = await Promise.all([
        api.get(`${PM}/invoices/${invId}`),
        api.get(`${PM}/invoices/${invId}/validation`).catch(() => ({ data: [] })),
      ]);
      setResult({
        invoice: inv.data,
        blocks: (inv.data && inv.data.block_reasons) || [],
        gates: Array.isArray(gates.data) ? gates.data : [],
      });
    } catch (e) {
      toast.error(errMsg(e, 'Could not finish this invoice'));
    } finally { setFinishing(false); }
  }, [invoice, commitRow, overBudget, confirm]);

  const resetForNext = useCallback((keepProvider) => {
    setResult(null);
    setInvoice(null);
    setDoc(null);
    setChecks({});
    setRows([blankRow()]);
    setHead((h) => emptyHeader(keepProvider ? { provider_id: h.provider_id } : null));
    setPendingFocus(null);
    if (providerRef.current) providerRef.current.focus();
  }, []);

  /* Ctrl/Cmd+Enter anywhere = start the header, or finish the invoice. The
     handler is held in a ref so the listener is bound once and never churns
     while the operator is typing. */
  const shortcutRef = useRef(() => {});
  useEffect(() => {
    shortcutRef.current = () => {
      if (result) return;
      if (invoice) finish(); else startInvoice(false);
    };
  }, [invoice, result, finish, startInvoice]);
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== 'Enter') return;
      e.preventDefault();
      shortcutRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ── render ──────────────────────────────────────────────────────────────── */

  if (pickers.loading) {
    return <div className="bg-white border border-slate-200 rounded-xl overflow-hidden"><TableSkeleton rows={8} cols={6} /></div>;
  }
  if (pickers.forbidden) {
    return <div className="bg-white border border-slate-200 rounded-xl"><AccessDenied message={pickers.error} /></div>;
  }
  if (pickers.error) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl">
        <ErrorState message={pickers.error} onRetry={() => setPickerNonce((n) => n + 1)} />
      </div>
    );
  }

  const afterSave = planRemaining == null ? null : round2(planRemaining - invoiceTotal);
  const overPlan = afterSave != null && afterSave < 0;
  const noDoc = !doc && !(invoice && invoice.document_url);

  return (
    <div className="space-y-4">
      <Toolbar
        title="Invoice entry" icon={Receipt}
        count={rows.filter((r) => r.state === 'saved').length} countLabel="lines saved"
        actions={(
          <>
            <Tooltip label={head.client_id ? 'Copy the lines from an earlier invoice' : 'Choose a participant first'}>
              <button type="button" className="btn-secondary btn-sm" disabled={!head.client_id}
                onClick={() => setCloneOpen(true)}>
                <Copy size={13} />Clone
              </button>
            </Tooltip>
            {onExit && <button type="button" className="btn-ghost btn-sm" onClick={onExit}>Close</button>}
          </>
        )}
      >
        {/* The budget position rides INSIDE the toolbar so it is pinned at every
            viewport width — an operator must never have to scroll to find out
            what is left. */}
        <div className="w-full border-t border-slate-100 pt-2.5">
          <StatRow cols={4}>
            <MoneyStat
              label="Plan remaining" value={planRemaining}
              sub={chosenContract ? chosenContract.ref : 'Choose a plan'}
              tone={planRemaining != null && planRemaining <= 0 ? 'rose' : 'slate'}
            />
            <MoneyStat
              label="This invoice" value={invoiceTotal}
              sub={savedTotal !== invoiceTotal ? `${money(savedTotal)} committed` : `${rows.filter((r) => r.state === 'saved').length} lines`}
            />
            <MoneyStat
              label="Plan after saving" value={afterSave} icon={ArrowRight}
              tone={overPlan ? 'rose' : afterSave != null ? 'emerald' : 'slate'}
              sub={overPlan ? 'This invoice exceeds the plan' : 'Projected'}
            />
            <MoneyStat
              label={categoryPositions.length === 1 ? categoryPositions[0].label : 'Categories touched'}
              currency={categoryPositions.length === 1}
              value={categoryPositions.length === 1
                ? round2(categoryPositions[0].remaining - categoryPositions[0].draw)
                : (categoryPositions.length || '—')}
              tone={categoryPositions.some((c) => c.remaining - c.draw < 0) ? 'rose' : categoryPositions.length ? 'emerald' : 'slate'}
              sub={categoryPositions.length === 1 ? `${money(categoryPositions[0].remaining)} before this invoice` : 'After saving'}
            />
          </StatRow>
        </div>
      </Toolbar>

      {/* ── 1 · The cascade ─────────────────────────────────────────────────── */}
      <SectionCard
        title={invoice ? 'Invoice' : 'Where this invoice came from'}
        icon={Users}
        actions={invoice ? <span className="badge badge-blue">Started</span> : null}
        bodyClass="p-4 space-y-3.5"
      >
        {invoice ? (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div><MicroLabel>Provider</MicroLabel><p className="text-[13px] text-slate-800 mt-0.5">{chosenProvider ? chosenProvider.name : '—'}</p></div>
            <div><MicroLabel>Participant</MicroLabel><p className="text-[13px] text-slate-800 mt-0.5">{chosenParticipant ? chosenParticipant.name : '—'}</p></div>
            <div><MicroLabel>Plan</MicroLabel><p className="text-[13px] text-slate-800 mt-0.5">{chosenContract ? chosenContract.ref : 'None linked'}</p></div>
            <div><MicroLabel>Invoice #</MicroLabel><p className="text-[13px] text-slate-800 mt-0.5 tabular-nums">{head.provider_invoice_number || '—'}</p></div>
            <div><MicroLabel>Invoice date</MicroLabel><p className="text-[13px] text-slate-800 mt-0.5 tabular-nums">{fmtDate(head.invoice_date)}</p></div>
            <div className="ml-auto min-w-[16rem]">
              <AttachmentField doc={doc} onDoc={attachLater} locked={!!(invoice && invoice.document_url)} />
            </div>
          </div>
        ) : (
          <>
            {/* Provider → Participant → Plan. Each field is the answer to the one
                before it, which is the order an invoice actually gives them up. */}
            <div className="grid gap-x-4 gap-y-3.5 grid-cols-1 lg:grid-cols-3">
              <Field label="1 · Provider" required hint={chosenProvider && chosenProvider.abn ? `ABN ${chosenProvider.abn}` : 'Who sent the invoice'}>
                {(id) => (
                  <Combo id={id} inputRef={providerRef} value={head.provider_id} options={providerOpts}
                    placeholder="Search providers" onChange={(v) => setHead((h) => ({ ...h, provider_id: v }))} />
                )}
              </Field>
              <Field label="2 · Participant" required hint={chosenParticipant && chosenParticipant.ndis_number ? `NDIS ${chosenParticipant.ndis_number}` : 'Who the support was for'}>
                {(id) => (
                  <Combo id={id} value={head.client_id} options={participantOpts}
                    placeholder="Search participants"
                    onChange={(v) => { setParticipantMatch(null); setHead((h) => ({ ...h, client_id: v, plan_id: '' })); }} />
                )}
              </Field>
              {/* A scan that could not identify the participant used to leave this field blank with
                  no explanation. Say what the invoice claims, and offer the people it could be. */}
              {participantMatch && !participantMatch.matched && !head.client_id && (
                <div className="sm:col-span-2 lg:col-span-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <p className="text-[12px] font-semibold text-amber-900">
                    {participantMatch.extracted_name || participantMatch.extracted_ndis
                      ? <>Invoice is for “{participantMatch.extracted_name || participantMatch.extracted_ndis}”</>
                      : 'The invoice does not name a participant'}
                  </p>
                  <p className="text-[11.5px] text-amber-800 mt-0.5">
                    {participantMatch.reason === 'ambiguous' ? 'More than one participant matches — pick the right one.'
                      : participantMatch.reason === 'near_match' ? 'Closest match:'
                        : participantMatch.reason === 'not_on_invoice' ? 'Choose the participant below.'
                          : 'No participant matches — choose one below.'}
                    {participantMatch.extracted_ndis && participantMatch.extracted_name
                      ? <span className="block tabular-nums text-amber-700 mt-0.5">NDIS {participantMatch.extracted_ndis}</span>
                      : null}
                  </p>
                  {participantMatch.candidates && participantMatch.candidates.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {participantMatch.candidates.map((c) => (
                        <button key={c.id} type="button"
                          onClick={() => { setParticipantMatch(null); setHead((h) => ({ ...h, client_id: c.id, plan_id: '' })); }}
                          className="text-[11.5px] font-semibold px-2 py-1 rounded-lg bg-white border border-amber-300 text-amber-900 hover:bg-amber-100">
                          {c.name}{c.ndis_number ? ` · ${c.ndis_number}` : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Field
                label="3 · Plan / service contract"
                hint={contracts.loading ? 'Loading plans…'
                  : chosenContract ? `${money(chosenContract.remaining)} remaining`
                    : head.client_id ? 'Optional, but without it no budget can be checked' : 'Choose a participant first'}
                error={contracts.error || undefined}
              >
                {(id) => (
                  <Combo id={id} value={head.plan_id} options={contractOpts} disabled={!head.client_id || contracts.loading}
                    placeholder={head.client_id ? 'Choose a plan' : 'Participant first'}
                    onChange={(v) => setHead((h) => ({ ...h, plan_id: v }))} />
                )}
              </Field>
            </div>

            <div className="grid gap-x-4 gap-y-3.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Provider invoice #" hint="Provider + number is the duplicate key">
                {(id) => (
                  <input id={id} type="text" className={clsx(CTRL, 'w-full tabular-nums')}
                    value={head.provider_invoice_number}
                    onChange={(e) => setHead((h) => ({ ...h, provider_invoice_number: e.target.value }))} />
                )}
              </Field>
              <Field label="Invoice date">
                {(id) => (
                  <input id={id} type="date" className={clsx(CTRL, 'w-full tabular-nums')}
                    value={head.invoice_date}
                    onChange={(e) => setHead((h) => ({ ...h, invoice_date: e.target.value }))} />
                )}
              </Field>
              <Field label="Provider invoice document" required
                hint="Drop the PDF or image. Reading it prefills the lines.">
                <AttachmentField doc={doc} onDoc={setDoc} onScan={scanDoc} scanning={scanning} />
              </Field>
            </div>

            {noDoc && (
              <p className="flex items-start gap-1.5 text-[11.5px] text-rose-600">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                <span>
                  Without the document this invoice cannot be approved, claimed or paid. You can
                  start now and attach it before you finish.
                </span>
              </p>
            )}

            {dupe && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3">
                <p className="text-[12.5px] font-semibold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle size={13} />Already on record
                </p>
                <p className="text-[12px] text-slate-700 mt-1">{dupe.message}</p>
                <dl className="flex flex-wrap gap-x-6 gap-y-1 mt-2">
                  <div><MicroLabel as="dt">Matched invoice</MicroLabel><dd className="text-[12.5px] text-slate-800 tabular-nums">#{dupe.match.provider_invoice_number || dupe.match.id.slice(0, 8)}</dd></div>
                  <div><MicroLabel as="dt">Total</MicroLabel><dd className="text-[12.5px] text-slate-800 tabular-nums">{money(dupe.match.total_amount)}</dd></div>
                  <div><MicroLabel as="dt">Status</MicroLabel><dd className="text-[12.5px] text-slate-800">{dupe.match.status}</dd></div>
                </dl>
                <div className="flex items-center gap-2 mt-3">
                  {onOpenInvoice && (
                    <button type="button" className="btn-secondary btn-sm" onClick={() => onOpenInvoice(dupe.match.id)}>
                      Open that invoice
                    </button>
                  )}
                  <button type="button" className="btn-secondary btn-sm" onClick={() => startInvoice(true)} disabled={starting}>
                    Create anyway
                  </button>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => setDupe(null)}>Cancel</button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button type="button" className="btn-primary btn-sm" disabled={!canStart || starting}
                onClick={() => startInvoice(false)}>
                {starting ? <Loader2 size={13} className="animate-spin motion-reduce:animate-none" /> : <Plus size={13} />}
                Start invoice
              </button>
              <span className="text-[11.5px] text-slate-400 flex items-center gap-1">
                <Kbd>Ctrl</Kbd><Kbd>↵</Kbd> to start — the grid goes live once the invoice exists
              </span>
            </div>
          </>
        )}
      </SectionCard>

      {/* ── 2 · The grid ────────────────────────────────────────────────────── */}
      <SectionCard
        title="Lines" icon={List}
        actions={(
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 flex-wrap justify-end">
            <span className="flex items-center gap-1"><Kbd>Tab</Kbd> next cell</span>
            <span className="text-slate-200">·</span>
            <span className="flex items-center gap-1"><Kbd>↵</Kbd> save + next line</span>
            <span className="text-slate-200">·</span>
            <span className="flex items-center gap-1"><Kbd>Ctrl</Kbd><Kbd>D</Kbd> duplicate above</span>
            <span className="text-slate-200">·</span>
            <span className="flex items-center gap-1"><Kbd>Ctrl</Kbd><Kbd>⌫</Kbd> delete line</span>
          </div>
        )}
      >
        {invoice && heldNotice && (
          <div role="alert" className="mx-4 mt-4 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3">
            <p className="text-[13px] font-semibold text-rose-900 flex items-center gap-1.5">
              <AlertTriangle size={14} /> This invoice is on hold — it needs a plan manager's approval
            </p>
            <p className="text-[12.5px] text-rose-800 mt-1.5">{heldNotice}</p>
            <p className="text-[12px] text-rose-800 mt-2">
              The line has been <strong>recorded</strong>, not accepted. <strong>Nothing on this invoice
              will be claimed</strong> — including the lines that are funded — until the shortfall is
              resolved. Reduce the amount, move the line to a category that has funding, wait for the next
              funding release, or ask the provider for a corrected invoice.
            </p>
          </div>
        )}

        {invoice && (overBudget.cats.length > 0 || overBudget.planOver > 0) && (
          <div role="alert" className="mx-4 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
            <p className="text-[13px] font-semibold text-rose-900 flex items-center gap-1.5">
              <AlertTriangle size={14} />
              This invoice is over budget
            </p>
            <ul className="mt-2 space-y-1">
              {overBudget.cats.map((c) => (
                <li key={c.key} className="text-[12.5px] text-rose-800">
                  <span className="font-semibold">{c.label}</span> — {money(c.remaining)} remaining,
                  {' '}{money(c.drawn)} on this invoice
                  {' '}<span className="font-semibold">({money(c.over)} over)</span>
                </li>
              ))}
              {overBudget.planOver > 0 && (
                <li className="text-[12.5px] text-rose-800">
                  <span className="font-semibold">Plan total</span> — {money(overBudget.planRemaining)} remaining,
                  {' '}{money(overBudget.invoiceTotal)} on this invoice
                  {' '}<span className="font-semibold">({money(overBudget.planOver)} over)</span>
                </li>
              )}
            </ul>
            <p className="text-[11.5px] text-rose-700 mt-2">
              Reduce the amounts, move a line to a category that has funding, or hold the invoice
              and ask the provider for a corrected one. The NDIA will not pay above the budget.
            </p>
          </div>
        )}

        {!invoice ? (
          <EmptyState
            icon={List} title="Start the invoice to enter lines"
            hint="Budget, price caps and the date window are all checked against a real invoice, so the header is committed first and then every line is validated as you type."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <caption className="sr-only">Invoice lines</caption>
              <thead>
                <tr className="border-b border-slate-200">
                  {[
                    ['', 'w-8'], ['Support item', 'min-w-[13rem]'], ['Category', 'min-w-[11rem]'], ['Service date', 'w-[8.5rem]'],
                    ['Measure', 'w-[6.5rem]'], ['Qty / hours', 'w-[6.5rem]'], ['Unit price', 'w-[7rem]'],
                    ['GST', 'w-[6.5rem]'], ['Claim type', 'w-[9rem]'], ['Amount', 'w-[7rem]'], ['', 'w-16'],
                  ].map(([h, w], i) => (
                    <th key={h || `c${i}`} scope="col"
                      className={clsx('py-2 px-2 font-semibold text-[10.5px] uppercase tracking-[0.07em] text-slate-400 whitespace-nowrap', w,
                        (h === 'Amount' || h === 'Unit price' || h === 'Qty / hours') && 'text-right')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const check = checks[row.key];
                  const headroom = headroomFor(row);
                  const catHeadroom = catHeadroomFor(row);
                  const block = rowBlock(row, check, headroom, catHeadroom);
                  const hard = !!(block && block.tone === 'rose');
                  const amount = amountOf(row);
                  const catLabel = check && check.category_remaining ? check.category_remaining.label : null;
                  return (
                    <React.Fragment key={row.key}>
                      <tr
                        onKeyDown={(e) => onRowKeyDown(e, row, i)}
                        className={clsx('border-b border-slate-100 border-l-2 transition-colors motion-reduce:transition-none',
                          row.state === 'error' || hard ? 'border-l-rose-400 bg-rose-50/30'
                            : row.state === 'saved' ? 'border-l-emerald-400'
                              : row.cloned ? 'border-l-amber-300' : 'border-l-transparent')}
                      >
                        <td className="py-1 px-2 text-center align-top">
                          {row.state === 'saving' ? <Loader2 size={12} className="text-slate-400 animate-spin motion-reduce:animate-none mx-auto mt-1.5" />
                            : row.state === 'saved' ? <Check size={12} className="text-emerald-500 mx-auto mt-1.5" strokeWidth={3} />
                              : <span className="text-[10.5px] text-slate-300 tabular-nums leading-7">{i + 1}</span>}
                        </td>
                        <td className="py-1 px-2 align-top">
                          <ItemCell
                            row={row} planId={head.plan_id} blocked={hard}
                            registerInput={(el) => { itemRefs.current[row.key] = el; }}
                            onPick={(patch) => patchRow(row.key, { ...patch, state: 'draft', error: null })}
                          />
                        </td>
                        <td className="py-1 px-2 align-top">
                          {/* The support category is what the budget is drawn against, and until now
                              it could only be inherited from the picked support item — an operator
                              could neither see nor choose it. Each option carries what that category
                              has left once the rest of this invoice is counted. */}
                          {(() => {
                            const left = row.support_category ? catRemainingAfterInvoice(row.support_category, row.key) : null;
                            const over = left != null && amountOf(row) > left;
                            const known = catPositions.some((c) => c.category === row.support_category);
                            return (
                              <>
                                <select
                                  aria-label="Support category"
                                  value={row.support_category || ''}
                                  disabled={hard}
                                  onChange={(e) => patchRow(row.key, { support_category: e.target.value, state: 'draft', error: null })}
                                  className={clsx(CELL, over && 'border-rose-300 bg-rose-50/60',
                                    !row.support_category && 'text-slate-400')}
                                >
                                  <option value="">Select category…</option>
                                  {catPositions.map((c) => {
                                    const after = catRemainingAfterInvoice(c.category, row.key);
                                    return (
                                      <option key={c.category} value={c.category}>
                                        {c.label} ({money(after == null ? c.remaining : after)} remaining)
                                      </option>
                                    );
                                  })}
                                  {/* A category carried in from an extracted invoice or a cloned line
                                      may not be funded on this plan. Keep it visible and say so, rather
                                      than silently dropping the operator's value. */}
                                  {row.support_category && !known && (
                                    <option value={row.support_category}>{row.support_category} (not funded on this plan)</option>
                                  )}
                                </select>
                                {over && (
                                  // Name the cap AND what was entered — "over budget" alone leaves the
                                  // operator to work out by how much before they can correct the line.
                                  <p className="text-[10.5px] text-rose-600 mt-1 leading-tight">
                                    Amount cannot exceed the remaining budget of {money(left)} ({money(amountOf(row))} entered)
                                  </p>
                                )}
                                {row.support_category && !known && catPositions.length > 0 && (
                                  <p className="text-[10.5px] text-amber-600 mt-1 leading-tight">Not funded on this plan</p>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="py-1 px-2 align-top">
                          <input
                            type="date" aria-label="Service date" value={row.item_date}
                            onChange={(e) => patchRow(row.key, { item_date: e.target.value, cloned: false, error: null })}
                            className={clsx(CELL, 'tabular-nums', row.cloned && 'border-amber-300 bg-amber-50/40')}
                          />
                          {row.cloned && <span className="block px-1.5 text-[10px] text-amber-600 leading-tight">confirm date</span>}
                        </td>
                        <td className="py-1 px-2 align-top">
                          {/* Exactly one of quantity or hours, so this is a toggle
                              and not two fields anybody could fill together. */}
                          <select
                            aria-label="Measure" value={row.measure}
                            onChange={(e) => patchRow(row.key, {
                              measure: e.target.value,
                              qty: e.target.value === 'qty' ? (row.qty || '1') : '',
                              hours: e.target.value === 'hours' ? (row.hours || '') : '',
                              error: null,
                            })}
                            className={clsx(CELL, 'cursor-pointer')}
                          >
                            <option value="qty">Quantity</option>
                            <option value="hours">Hours</option>
                          </select>
                        </td>
                        <td className="py-1 px-2 align-top">
                          {row.measure === 'hours' ? (
                            <>
                              <input
                                type="text" inputMode="decimal" aria-label="Hours" placeholder="2:30 or 2.5"
                                value={row.hours}
                                onChange={(e) => patchRow(row.key, { hours: e.target.value, error: null })}
                                className={clsx(CELL, 'tabular-nums text-right')}
                              />
                              {parseHours(row.hours) > 0 && (
                                <span className="block px-1.5 text-[10px] text-slate-400 text-right tabular-nums leading-tight">
                                  {toHHMM(parseHours(row.hours))} claimed
                                </span>
                              )}
                            </>
                          ) : (
                            <input
                              type="number" step="0.01" min="0" aria-label="Quantity" value={row.qty}
                              onChange={(e) => patchRow(row.key, { qty: e.target.value, error: null })}
                              className={clsx(CELL, 'tabular-nums text-right')}
                            />
                          )}
                        </td>
                        <td className="py-1 px-2 align-top">
                          {/* The NDIS price guide sets this rate. It was freely typeable, so an
                              operator could put any number in and only learn it was over the cap
                              after the line refused to approve — or, worse, quietly claim under the
                              cap at a figure nobody chose.

                              So it is locked to the published rate whenever the price book has one.
                              A provider may legitimately charge LESS than the cap, so the lock can
                              be released per line — deliberately, with the reason visible — rather
                              than being absolute. Over the cap is still refused. */}
                          <input
                            type="number" step="0.01" min="0" aria-label="Unit price" value={row.unit_price}
                            readOnly={check && check.cap != null && !row.price_unlocked}
                            title={check && check.cap != null && !row.price_unlocked
                              ? `Set by the NDIS price guide at ${money(check.cap)}. Choose "charge less" to record a lower agreed rate.`
                              : undefined}
                            onChange={(e) => patchRow(row.key, { unit_price: e.target.value, error: null })}
                            className={clsx(CELL, 'tabular-nums text-right',
                              check && check.cap != null && !row.price_unlocked && 'bg-slate-50 text-slate-600 cursor-not-allowed',
                              check && check.cap != null && Number(row.unit_price) > Number(check.cap) + 0.005 && 'border-rose-300 bg-rose-50 text-rose-700')}
                          />
                          {check && check.cap != null && (
                            <span className={clsx('block px-1.5 text-[10px] text-right tabular-nums leading-tight',
                              Number(row.unit_price) > Number(check.cap) + 0.005 ? 'text-rose-600 font-semibold' : 'text-slate-400')}>
                              {row.price_unlocked ? (
                                <button type="button" className="underline hover:text-slate-600"
                                  onClick={() => patchRow(row.key, { price_unlocked: false, unit_price: String(check.cap), error: null })}>
                                  back to guide {money(check.cap)}
                                </button>
                              ) : (
                                <>
                                  guide {money(check.cap)}
                                  <button type="button" className="ml-1.5 underline hover:text-slate-600"
                                    onClick={() => patchRow(row.key, { price_unlocked: true, error: null })}>
                                    charge less
                                  </button>
                                </>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="py-1 px-2 align-top">
                          <select
                            aria-label="GST code" value={row.gst_code}
                            onChange={(e) => patchRow(row.key, { gst_code: e.target.value, error: null })}
                            className={clsx(CELL, 'cursor-pointer')}
                          >
                            {GST_CODES.map((g) => <option key={g.value} value={g.value}>{g.value}</option>)}
                          </select>
                        </td>
                        <td className="py-1 px-2 align-top">
                          <select
                            aria-label="Claim type" value={row.claim_type}
                            onChange={(e) => patchRow(row.key, {
                              claim_type: e.target.value,
                              cancellation_reason: e.target.value === 'CANC' ? row.cancellation_reason : '',
                              error: null,
                            })}
                            className={clsx(CELL, 'cursor-pointer', row.claim_type && 'font-medium')}
                          >
                            {CLAIM_TYPES.map((c) => <option key={c.value || 'blank'} value={c.value}>{c.value || '—'}</option>)}
                          </select>
                          {row.claim_type === 'CANC' && (
                            <select
                              aria-label="Cancellation reason" value={row.cancellation_reason}
                              onChange={(e) => patchRow(row.key, { cancellation_reason: e.target.value, error: null })}
                              className={clsx(CELL, 'cursor-pointer mt-1', !row.cancellation_reason && 'border-rose-300 bg-rose-50')}
                            >
                              <option value="">Reason…</option>
                              {CANCELLATION_REASONS.map((c) => <option key={c.value} value={c.value}>{c.value}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="py-1 px-2 align-top text-right">
                          <span className={clsx('inline-block leading-7 tabular-nums font-medium',
                            hard ? 'text-rose-600' : row.state === 'saved' ? 'text-slate-800' : 'text-slate-500')}>
                            {amount > 0 ? money(amount) : '—'}
                          </span>
                        </td>
                        <td className="py-1 px-2 align-top text-right whitespace-nowrap">
                          <Tooltip label="Duplicate the line above" side="left">
                            <button type="button" onClick={() => duplicateFrom(row.key)} disabled={i === 0}
                              aria-label="Duplicate the line above"
                              className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent">
                              <Copy size={13} />
                            </button>
                          </Tooltip>
                          <Tooltip label="Remove this line" side="left">
                            <button type="button" onClick={() => removeRow(row.key)} aria-label="Remove this line"
                              className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                              <Trash2 size={13} />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>

                      {/* The row's own verdict, under the row it belongs to — a
                          budget answer three columns away is a budget answer
                          nobody reads. */}
                      {(row.error || block || catLabel || (check && check.failed)) && (
                        <tr className="border-b border-slate-100">
                          <td />
                          <td colSpan={10} className="px-2 pb-1.5">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
                              {row.error && (
                                <span className="flex items-center gap-1 text-rose-600 font-medium">
                                  <AlertTriangle size={11} />{row.error}
                                </span>
                              )}
                              {!row.error && block && (
                                <span className={clsx('flex items-center gap-1',
                                  block.tone === 'rose' ? 'text-rose-600 font-medium' : 'text-slate-400')}>
                                  {block.tone === 'rose' && <AlertTriangle size={11} />}{block.text}
                                </span>
                              )}
                              {check && check.failed && <span className="text-amber-600">{check.failed}</span>}
                              {catLabel && (
                                <span className={clsx('tabular-nums', catHeadroom != null && catHeadroom - amount < 0 ? 'text-rose-600' : 'text-slate-400')}>
                                  {catLabel}: {money(catHeadroom == null ? 0 : catHeadroom)} available
                                  {amount > 0 && catHeadroom != null ? ` → ${money(catHeadroom - amount)} after` : ''}
                                </span>
                              )}
                              {headroom != null && (
                                <span className={clsx('tabular-nums', headroom - amount < 0 ? 'text-rose-600' : 'text-slate-400')}>
                                  Plan: {money(headroom)} available
                                  {amount > 0 ? ` → ${money(headroom - amount)} after` : ''}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50/70">
                  <td colSpan={9} className="px-2 py-2">
                    <button type="button" className="btn-ghost btn-sm" onClick={() => addRow({ gst_code: 'P2' })}>
                      <Plus size={13} />Add line
                    </button>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Currency value={invoiceTotal} className="text-[13px] font-bold text-slate-900" />
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Every category this invoice draws on, before and after. */}
      {categoryPositions.length > 1 && (
        <SectionCard title="Category positions" bodyClass="p-4">
          <StatRow cols={Math.min(categoryPositions.length, 4)}>
            {categoryPositions.slice(0, 4).map((c) => (
              <MoneyStat
                key={c.label} label={c.label} value={round2(c.remaining - c.draw)} size="sm"
                tone={c.remaining - c.draw < 0 ? 'rose' : 'emerald'}
                sub={`${money(c.draw)} of ${money(c.remaining)} drawn here`}
              />
            ))}
          </StatRow>
        </SectionCard>
      )}

      {/* ── 3 · Finish + the validation gates ──────────────────────────────── */}
      {invoice && !result && (
        <div className="flex items-center gap-3 flex-wrap">
          <button type="button" className="btn-primary btn-sm" onClick={finish} disabled={finishing}>
            {finishing ? <Loader2 size={13} className="animate-spin motion-reduce:animate-none" /> : <CheckCircle2 size={13} />}
            Finish and check
          </button>
          <span className="text-[11.5px] text-slate-400 flex items-center gap-1">
            <Kbd>Ctrl</Kbd><Kbd>↵</Kbd> · commits anything still in draft, then runs every gate
          </span>
          {noDoc && (
            <span className="text-[11.5px] text-rose-600 flex items-center gap-1 ml-auto">
              <AlertTriangle size={12} />No document attached
            </span>
          )}
        </div>
      )}

      {result && (
        <SectionCard
          title="Checked" icon={FileText}
          actions={(
            <div className="flex items-center gap-2">
              {onOpenInvoice && (
                <button type="button" className="btn-secondary btn-sm" onClick={() => onOpenInvoice(result.invoice.id)}>
                  Open invoice
                </button>
              )}
              <button type="button" className="btn-secondary btn-sm" onClick={() => resetForNext(true)}>
                <Plus size={13} />Next from this provider
              </button>
              <button type="button" className="btn-ghost btn-sm" onClick={() => resetForNext(false)}>
                <RefreshCw size={13} />New
              </button>
            </div>
          )}
          bodyClass="p-4 space-y-4"
        >
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <div><MicroLabel>Invoice total</MicroLabel><p className="text-[17px] font-semibold text-slate-900 tabular-nums mt-0.5">{money(result.invoice.total_amount)}</p></div>
            <div><MicroLabel>Lines</MicroLabel><p className="text-[17px] font-semibold text-slate-900 tabular-nums mt-0.5">{(result.invoice.lines || []).length}</p></div>
            <div><MicroLabel>Reference</MicroLabel><p className="text-[13px] text-slate-700 mt-1 tabular-nums">{result.invoice.provider_invoice_number || '—'}</p></div>
          </div>

          <BlockReasonList
            reasons={result.blocks}
            emptyLabel="Every gate passed — this invoice is ready for approval."
          />

          {/* The full thirteen, passes included: knowing what was checked is the
              point of the panel, not only what failed. */}
          <div>
            <MicroLabel as="h3" className="mb-2">All checks</MicroLabel>
            <ul className="grid gap-1.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {[...result.gates].sort((a, b) => {
                const ai = GATE_ORDER.indexOf(a.gate); const bi = GATE_ORDER.indexOf(b.gate);
                return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
              }).map((g) => (
                <li key={g.gate} className="flex items-start gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                  {g.passed
                    ? <Check size={12} strokeWidth={3} className="mt-0.5 shrink-0 text-emerald-500" />
                    : <AlertTriangle size={12} className="mt-0.5 shrink-0 text-rose-500" />}
                  <div className="min-w-0">
                    <p className={clsx('text-[12px] font-medium', g.passed ? 'text-slate-600' : 'text-rose-700')}>
                      {GATE_LABEL[g.gate] || g.gate}
                    </p>
                    {g.detail && <p className="text-[11px] text-slate-400 truncate" title={g.detail}>{g.detail}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </SectionCard>
      )}

      {cloneOpen && head.client_id && (
        <ClonePicker
          clientId={head.client_id} providerId={head.provider_id}
          onClose={() => setCloneOpen(false)} onClone={applyClone}
        />
      )}
    </div>
  );
}
